'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useMemo, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph } from '../../../internal/icons.js';
const FieldContext = createContext(null);
/**
 * Groups a label, a control, help text and an error message, and wires their
 * ids together: the control gets `id`, `aria-describedby`, `aria-invalid`,
 * `aria-required` and `disabled` automatically. Descriptions and errors
 * register themselves, so `aria-describedby` only names elements that exist.
 *
 *   <Field invalid={!!error}>
 *     <FieldLabel>Email</FieldLabel>
 *     <Input type="email" />
 *     <FieldError>{error}</FieldError>
 *   </Field>
 */
export function Field({ id, invalid = false, required = false, disabled = false, layout = 'stack', className, children, ...rest }) {
    const auto = useId();
    const controlId = id ?? `mn-field-${auto}`;
    const [hasDescription, setHasDescription] = useState(false);
    const [hasError, setHasError] = useState(false);
    const register = useCallback((part, present) => {
        if (part === 'description')
            setHasDescription(present);
        else
            setHasError(present);
    }, []);
    const value = useMemo(() => ({
        controlId,
        labelId: `${controlId}-label`,
        descriptionId: `${controlId}-description`,
        errorId: `${controlId}-error`,
        invalid,
        required,
        disabled,
        hasDescription,
        hasError,
        register,
    }), [controlId, invalid, required, disabled, hasDescription, hasError, register]);
    return (_jsx(FieldContext.Provider, { value: value, children: _jsx("div", { ...rest, className: cx('mn-field', className), "data-invalid": invalid || undefined, "data-disabled": disabled || undefined, "data-layout": layout === 'row' ? 'row' : undefined, children: children }) }));
}
export function useFieldContext() {
    return useContext(FieldContext);
}
/**
 * Merges Field context into a control's props. Explicit props win, so a
 * control can still be used outside a Field.
 */
export function useFieldControl(props, options = {}) {
    const field = useContext(FieldContext);
    const invalid = options.invalid ?? field?.invalid ?? false;
    const describedBy = [
        props['aria-describedby'],
        field?.hasDescription ? field.descriptionId : undefined,
        field?.hasError && invalid ? field.errorId : undefined,
    ]
        .filter(Boolean)
        .join(' ');
    const required = props.required ?? field?.required;
    return {
        ...props,
        id: props.id ?? field?.controlId,
        disabled: props.disabled ?? (field?.disabled || undefined),
        required,
        'aria-describedby': describedBy || undefined,
        ...(invalid ? { 'aria-invalid': true } : {}),
        ...(required ? { 'aria-required': true } : {}),
    };
}
export const FieldLabel = forwardRef(function FieldLabel({ className, children, optionalText, htmlFor, ...rest }, ref) {
    const field = useContext(FieldContext);
    return (_jsxs("label", { ref: ref, ...rest, id: rest.id ?? field?.labelId, htmlFor: htmlFor ?? field?.controlId, className: cx('mn-field__label', className), children: [children, field?.required ? (_jsx("span", { className: "mn-field__required", "aria-hidden": true, children: "*" })) : optionalText ? (_jsx("span", { className: "mn-field__optional", children: optionalText })) : null] }));
});
function useRegister(part, present) {
    const field = useContext(FieldContext);
    const register = field?.register;
    useEffect(() => {
        if (!register || !present)
            return;
        register(part, true);
        return () => register(part, false);
    }, [register, part, present]);
    return field;
}
export function FieldDescription({ className, ...rest }) {
    const field = useRegister('description', true);
    return (_jsx("p", { ...rest, id: rest.id ?? field?.descriptionId, className: cx('mn-field__description', className) }));
}
/**
 * Renders nothing when empty, so it can always be present. Announced politely
 * when it appears.
 */
export function FieldError({ className, children, ...rest }) {
    const empty = children === null || children === undefined || children === false || children === '';
    const field = useRegister('error', !empty);
    if (empty)
        return null;
    return (_jsx("p", { ...rest, id: rest.id ?? field?.errorId, className: cx('mn-field__error', className), "aria-live": "polite", children: children }));
}
/**
 * The platform `<select>`, styled like Input and wired to Field. Use it where
 * the native picker is right: long option lists on mobile, forms that must
 * work without JavaScript, compact toolbar filters. For a styled listbox use
 * Select; for search use Combobox.
 */
export const NativeSelect = forwardRef(function NativeSelect({ size = 'md', invalid, options, placeholder, label, wrapperClassName, className, children, ...rest }, ref) {
    const auto = useId();
    const props = useFieldControl(rest, { invalid });
    const id = props.id ?? (label ? `mn-native-select-${auto}` : undefined);
    return (_jsxs("span", { className: cx('mn-native-select', wrapperClassName), "data-size": size, "data-inline-label": label ? '' : undefined, "data-disabled": props.disabled || undefined, children: [label ? (_jsx("label", { htmlFor: id, className: "mn-native-select__label", children: label })) : null, _jsxs("span", { className: "mn-native-select__box", children: [_jsxs("select", { ref: ref, ...props, id: id, "data-size": size, className: cx('mn-input', 'mn-native-select__control', className), children: [placeholder !== undefined ? (_jsx("option", { value: "", disabled: props.required || undefined, children: placeholder })) : null, options?.map((o) => (_jsx("option", { value: o.value, disabled: o.disabled, children: o.label }, o.value))), children] }), _jsx(ChevronDownGlyph, { className: "mn-native-select__chevron" })] })] }));
});
//# sourceMappingURL=form-field.js.map